import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import { getOrderItems, getOrderRelatedItemById, loadCompletedOrderById } from '@vista-digital/occ-domain'
import type { OccI18N } from '@vista-digital/occ-ui'
import { CurzonEcommerceDataLayer, GiftItem } from '@/common/services/models-gtm'
import { isCompletedGiftShopOrder } from '../../../utils/isCompletedGiftShopOrder'
import { gtm } from '../gtm.service'

export class GiftShopGtmEvents {
    private static get logger() {
        return getLogger('GiftShopGtmEvents').value
    }

    public static async beginCheckout(i18n: OccI18N) {
        try {
            const items = this.getGiftItems(i18n)
            if (!items.length) return
            await gtm.sendEcommerceDataLayer(new CurzonEcommerceDataLayer('begin_checkout', { items }))
        } catch (e) {
            this.logger.error('Error sending begin checkout event', e)
        }
    }

    public static async addToCart(i18n: OccI18N) {
        try {
            const items = this.getGiftItems(i18n)
            if (!items.length) return
            await gtm.sendEcommerceDataLayer(new CurzonEcommerceDataLayer('add_to_cart', { items }))
        } catch (e) {
            this.logger.error('Error sending add to cart event', e)
        }
    }

    public static async purchase(orderId: string, i18n: OccI18N) {
        try {
            const completedOrder = await loadCompletedOrderById(orderId)
            if (!isCompletedGiftShopOrder(completedOrder)) return
            const total = completedOrder?.totalPrice?.valueIncludingTax ?? 0.0
            const items = completedOrder.items.map(item => {
                const orderItem = getOrderRelatedItemById(item.itemId)
                const itemName = i18n.translatable(orderItem?.name ?? '')
                return new GiftItem({
                    item_name: itemName,
                    price: item.price.valueIncludingTax,
                    item_category: 'Gift',
                    quantity: 1,
                })
            })
            await gtm.sendEcommerceDataLayer(
                new CurzonEcommerceDataLayer('purchase', {
                    transaction_id: orderId,
                    value: total,
                    currency: 'GBP',
                    items,
                })
            )
        } catch (e) {
            this.logger.error('Error sending purchase event', e)
        }
    }

    private static getGiftItems(i18n: OccI18N) {
        return getOrderItems().map(item => {
            const orderItem = getOrderRelatedItemById(item.itemId)
            const itemName = i18n.translatable(orderItem?.name ?? '')
            const price = item.price.valueIncludingTax
            return new GiftItem({ item_name: itemName, price, item_category: 'Gift', quantity: 1 })
        })
    }
}
